import { z } from "zod";

/**
 * How OpenRouter serves one model: which hosts, at what price, how much
 * thinking. It travels with the model, because a host order or a price ceiling
 * tuned for one model is wrong for the next.
 */
const modelHosting = {
	/** OpenRouter provider slugs tried in this order (e.g. "coreweave/fp4"); empty lets OpenRouter choose. */
	providers: z.array(z.string().min(1).max(60)).max(5).default([]),
	/** How OpenRouter picks among hosts: "latency" follows its live measurements (host speed shifts by hours); null is its default balancing. */
	providerSort: z
		.enum(["latency", "throughput", "price"])
		.nullable()
		.default(null),
	/** USD per million tokens a host may charge; keeps latency routing off hosts several times the list price. */
	maxPricePerMillion: z
		.object({
			input: z.number().positive(),
			output: z.number().positive(),
		})
		.nullable()
		.default(null),
	/**
	 * Whether OpenRouter may serve the turn from a host outside `providers`.
	 * True keeps the turn alive when every pinned host is busy, but the
	 * pinned order is then only a preference: measured 2026-09-18, the
	 * hosts we fall through to carry the long tail (novita p50 1.33 s,
	 * max 9.81 s, 91% uptime). False bounds the tail and lets the
	 * model-level `fallbacks` chain catch a hard failure instead.
	 */
	allowFallbacks: z.boolean().default(true),
	/**
	 * How many of `providers` get the same request at once; the first to
	 * answer wins, the rest are cancelled (hedge.ts). 1 is a single request.
	 * One host's time to first word swings with its prompt cache: measured
	 * 2026-09-18, Gemma on Parasail p50 1.35 s / p90 3.2 s, raced with
	 * DeepInfra p50 0.62 s / p90 1.0 s.
	 */
	hedge: z.number().int().min(1).max(3).default(1),
	reasoningEffort: z.enum(["none", "low", "medium", "high"]).default("low"),
};

export const modelsSchema = z
	.object({
		primary: z.string().default("google/gemini-3.5-flash-lite"),
		/** Tried in order by OpenRouter when the primary errors or is down. */
		fallbacks: z.array(z.string()).max(3).default(["openai/gpt-5.6-luna"]),
		...modelHosting,
		/**
		 * A different model for shoppers writing in some languages, e.g. the
		 * one with the best Lebanese for Arabic and Arabizi and a faster one
		 * for everyone else. First match wins; no match uses `primary`. The
		 * language is the one language.ts reads off the message, so routing
		 * costs nothing.
		 */
		routes: z
			.array(
				z.object({
					languages: z.array(z.string().min(1).max(40)).min(1),
					primary: z.string(),
					...modelHosting,
				}),
			)
			.max(4)
			.default([]),
		/**
		 * Who runs a recipe turn. Its steps are long tool calls the shopper
		 * waits through (the recipe itself, then often a basket of its
		 * ingredients), so the whole turn goes to the fastest writer. Used
		 * when the relevance check reads the message as a recipe request.
		 * Measured 2026-09-19 on a 240-token recipe: Gemma 4 31B 9-13 s on
		 * Parasail, Gemini 3.1 Flash-Lite 1.1-1.4 s. Null: the turn's own
		 * model runs it.
		 */
		recipe: z
			.object({ primary: z.string(), ...modelHosting })
			.nullable()
			.default(null),
		/**
		 * Who reads a photo the shopper sent (a product, a shopping list, a dish) into a
		 * `[Photo: ...]` request before the turn. One structured call per photo; must be on the
		 * allow-list like every other model.
		 */
		vision: z.string().default("google/gemini-3.5-flash-lite"),
		temperature: z.number().min(0).max(1).default(0.3),
		maxOutputTokens: z.number().int().min(200).max(8000).default(1200),
		/** Tool-call rounds per shopper message before the turn is cut. */
		maxSteps: z.number().int().min(1).max(12).default(6),
	})
	.prefault({});

type ModelsSettings = z.infer<typeof modelsSchema>;
type ModelRoute = ModelsSettings["routes"][number];

/** A model and how it is hosted: the default, a language route, or the recipe writer. */
export type ModelHosting = Omit<ModelRoute, "languages">;

/** The model, and how it is hosted, for a shopper writing in `language`. */
export function modelRouteFor(
	models: ModelsSettings,
	language: string | null,
): ModelHosting {
	return (
		models.routes.find(
			(route) => language !== null && route.languages.includes(language),
		) ?? models
	);
}
